<template>
  <div v-if="error" class="app app--error">
    <div class="app-error">
      <h2 class="app-error__title">{{ errorTitle }}</h2>
      <p class="app-error__message">{{ error }}</p>

      <template v-if="isCentrifugeError">
        <p class="app-error__hint">
          We will redirect you to the home page in {{ redirectDelaySeconds }} seconds, where you can
          sign in again.
        </p>
        <Btn class="app-error__button" @click="redirectToHomeAndClearAuthTokens">
          Go to home now
        </Btn>
      </template>
    </div>
  </div>
  <div v-else-if="!isLoading" class="app-body__flexbox">
    <Header />
    <router-view />

    <!--Absolute positioned-->
    <PortalTarget name="modal" style="z-index: 999" multiple />
    <PortalTarget name="mobilePopup" style="z-index: 1000" multiple />
    <v-dialog />
    <ChangelogModal v-if="versions" :versions="versions" />
    <Faq />
  </div>
</template>

<script>
  import NotificationService from '@/services/NotificationService.ts';
  import Header from 'components/blocks/layouts/vueHeader/Header';
  import ChangelogModal from 'components/block/modal/vueChangelogModal/ChangelogModal';
  import * as Sentry from '@sentry/vue';

  import changelogParser from '@/assets/libraries/changelogParser';

  import CHANGELOG from './CHANGELOG.md';
  import VersionHelper from 'utils/VersionHelper.ts';
  import { AuthService } from '@/services/auth.service';
  import { connectToCentrifuge } from '@/services/CentrifugeService';
  import { persistentStorage, PersistentStorageKeys } from 'utils/persistentStorage';
  import { ERROR_CENTRIFUGE_NO_CONNECTION } from '@/constants/centrifuge/errors';
  import Faq from '@/components/blocks/reactComponents/Faq.vue';
  import Btn from '@/uikitBase/btns/Btn.vue';

  export default {
    name: 'App',

    components: {
      Btn,
      ChangelogModal,
      Header,
      Faq,
    },

    data: () => ({
      notificationService: null,

      versions: null,

      isLoading: true,
      error: null,
      errorCode: null,
      redirectDelaySeconds: 10,
      redirectTimerId: null,
    }),

    computed: {
      isCentrifugeError() {
        return this.errorCode === 'centrifuge_connection';
      },
      errorTitle() {
        return this.isCentrifugeError ? 'Connection problem' : 'Unexpected error';
      },
    },

    async created() {
      this.initSentry();
      await this.initCentrifuge();
      this.initNotificationService();
      this.initChangelog();
    },

    beforeDestroy() {
      this.notificationService?.disconnect();
      this.clearErrorTimers();
    },

    methods: {
      initNotificationService() {
        const uiInteractors = {};

        this.notificationService = new NotificationService(
          AuthService.userData().sub,
          {
            showWithActions: this.notifyWithActions,
            showError: this.notifyError,
            hide: this.notifyHide,
          },
          uiInteractors,
        );
      },
      async initChangelog() {
        if (!AuthService.isAuthenticated()) {
          // To handle the case if a user clears the local storage manually
          return;
        }

        const changelog = changelogParser({ text: CHANGELOG });

        if (changelog.versions.length) {
          const [{ version: latestVersion }] = changelog.versions;
          this.versions = changelog.versions;

          const lastUsedAppVersion = persistentStorage.get(
            PersistentStorageKeys.LAST_USED_APP_VERSION,
          );
          if (!lastUsedAppVersion || VersionHelper.greaterThan(latestVersion, lastUsedAppVersion)) {
            persistentStorage.set(PersistentStorageKeys.LAST_USED_APP_VERSION, latestVersion);

            await this.$nextTick();
            this.$modal.show('changelog-modal');
          }
        }
      },
      async initCentrifuge() {
        try {
          await connectToCentrifuge();
        } catch {
          this.error = ERROR_CENTRIFUGE_NO_CONNECTION;
          this.errorCode = 'centrifuge_connection';
          this.startCentrifugeErrorHandling();
        } finally {
          this.isLoading = false;
        }
      },
      startCentrifugeErrorHandling() {
        this.clearErrorTimers();

        this.redirectTimerId = window.setTimeout(() => {
          this.redirectToHomeAndClearAuthTokens();
        }, this.redirectDelaySeconds * 1000);
      },
      clearErrorTimers() {
        if (this.redirectTimerId) {
          window.clearTimeout(this.redirectTimerId);
          this.redirectTimerId = null;
        }
      },
      redirectToHomeAndClearAuthTokens() {
        this.clearErrorTimers();
        persistentStorage.remove(PersistentStorageKeys.ACCESS_TOKEN_KEY, {
          isForActiveUser: false,
        });
        persistentStorage.remove(PersistentStorageKeys.REFRESH_TOKEN_KEY, {
          isForActiveUser: false,
        });
        persistentStorage.remove(PersistentStorageKeys.EXPIRY, { isForActiveUser: false });

        window.location.replace('/app/');
      },
      initSentry() {
        const user = AuthService.userData();

        if (user) {
          Sentry.setUser({
            id: user.user_id,
            organizationId: user.organization_id,
            isAdmin: user.is_superuser,
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .app {
    &--error {
      display: flex;
      min-height: 100vh;
      padding: 24px;
      align-items: center;
      justify-content: center;
      background: $color-bg-second;
    }
  }

  .app-error {
    max-width: 560px;
    padding: 24px;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);

    &__title {
      margin: 0 0 12px;
      font-size: 24px;
      line-height: 1.3;
    }

    &__message {
      margin: 0 0 8px;
      color: $color-text-danger;
      font-size: 16px;
      line-height: 1.5;
    }

    &__hint {
      margin: 0 0 20px;
      color: $color-text-second;
      font-size: 14px;
      line-height: 1.5;
    }
  }
</style>
